import { apiPath, clientHeader, contextPath, defaultProxyPath, prefixHeader, versionHeader } from "@churchofjesuschrist/maps-constants";
import { BoundingBox, Position, ResponseType, Size } from "@churchofjesuschrist/maps-models";
import { defaultEmpty, delay, generateGuid, hashCode, isArray, isBrowser, isEmpty, isNotEmpty, isObject, isString, noop, proxy, removeUndefined, resolveValue, toArray } from "@churchofjesuschrist/maps-utils";
import Convertible from "./components/Convertible";
import { compileIdentifiable, deserializeCluster, deserializeLocation, locationsOperation, refinableOperation, serializeColor, serializeFilters, serializeIdentifiable, serializeIdentifiables, serializeIntersections, serializeMeasurement, serializeMeasurements, serializeStyles, serializeWaypoint, serializeWaypoints } from "./components/Serializer";
import { version } from "./version";
class $MapsService {
  constructor() {
    if (isBrowser) {
      if (window.MapsService) {
        if (!(window.MapsService instanceof $MapsService)) {
          console.warn("Global MapsService variable is not of type MapsService.  There may be multiple class definitions for MapsService");
        }
        if (window.MapsService instanceof $MapsService) {
          return window.MapsService;
        }
      } else {
        window.MapsService = this;
      }
    }

    /** @internal */
    this.$config = {
      init: false,
      production: null,
      develop: null,
      proxy: null,
      client: null,
      language: null,
      coordinates: null,
      legal: null,
      strings: {},
      session: generateGuid().replace(/-/g, "").substring(0, 16),
      request: 0,
      basicAuth: null,
      oAuth: null,
      provider: null,
      converter: null,
      configPromise: null,
      providerPromise: null
    };
    this.$refreshConfig();
  }

  //region Private

  /** @internal */
  $refreshConfig(configure = null) {
    let updates = configure || (isBrowser ? window.MAPS_SERVICE_CONFIG : null);
    if (updates) {
      Object.assign(this.$config, {
        init: defaultEmpty(updates.init, this.$config.init),
        production: defaultEmpty(this.$config.production, updates.production),
        develop: defaultEmpty(this.$config.develop, updates.develop),
        proxy: defaultEmpty(this.$config.proxy, updates.proxy),
        client: defaultEmpty(this.$config.client, updates.client),
        language: defaultEmpty(this.$config.language, updates.language || updates.locale),
        legal: defaultEmpty(this.$config.legal, updates.legal),
        strings: {
          ...this.$config.strings,
          ...(updates.strings || {})
        },
        oAuth: defaultEmpty(this.$config.oAuth, updates.oAuth),
        provider: defaultEmpty(updates.provider, this.$config.provider),
        coordinates: defaultEmpty(updates.coordinates, this.$config.coordinates),
        nonce: defaultEmpty(updates.nonce, this.$config.nonce)
      });
    }
    if (isBrowser) {
      window.MAPS_SERVICE_CONFIG = this.$config;
    }
  }

  /** @internal */
  $tokenize(...args) {
    const {
      session,
      nonce
    } = this.$config;
    let arg = args.join("");
    if (arg && session && nonce) {
      let hash = hashCode(arg + session + nonce);
      return hash.toString(36).replace("-", "");
    }
  }

  /** @internal */
  $url(path, parameters, bypassToken = false) {
    const {
      production,
      develop,
      proxy
    } = this.$config;
    let subDomain = production ? "maps-service" : "maps-service-int";
    if (develop) {
      subDomain = "maps-service-dev";
    }
    let root = `https://${subDomain}.churchofjesuschrist.org`;
    if (proxy) {
      root = isString(proxy) ? proxy : defaultProxyPath;
      if (root[0] === "/" && isBrowser) {
        root = window.location.origin + root;
      }
    } else {
      root += contextPath;
    }
    let params = [];
    if (parameters) {
      Object.entries(parameters).forEach(([key, value]) => {
        if (isNotEmpty(value)) {
          let val = value;
          if (isArray(val)) {
            val = val.map(encodeURIComponent).join(",");
          } else {
            val = encodeURIComponent(val);
          }
          params.push(`${key}=${val}`);
        }
      });
    }
    if (!bypassToken) {
      let token = this.$tokenize(path, ...params);
      if (token) {
        params.push(`token=${token}`);
      }
    }
    let query = "";
    if (params.length) {
      query = "?" + params.join("&");
    }
    return `${root}${apiPath}${path}${query}`;
  }

  /** @internal */
  $urlClient(path, parameters, {
    language
  } = {}) {
    let params = {
      ...parameters,
      client: this.$config.client,
      version: version,
      lang: language || this.$config.language,
      trace: this.$config.session
    };
    return this.$url(path, params, true);
  }

  /** @internal */
  $userTokens() {
    let tokens = resolveValue(this.$config.oAuth);
    if (tokens) {
      if (tokens === true) {
        //TODO: login to maps own proxy server
      }
      if (isBrowser && (tokens.cookiePrefix || tokens.cookies || tokens.cookie)) {
        if (tokens.cookiePrefix || tokens.cookies) {
          tokens = {
            accessToken: this.$cookieValue(tokens.cookiePrefix ? tokens.cookiePrefix + "_access_token" : tokens.cookies.access),
            refreshToken: this.$cookieValue(tokens.cookiePrefix ? tokens.cookiePrefix + "_refresh_token" : tokens.cookies.refresh),
            idToken: this.$cookieValue(tokens.cookiePrefix ? tokens.cookiePrefix + "_identity_token" : tokens.cookies.identity)
          };
        } else {
          tokens = this.$cookieValue(tokens.cookie);
        }
      }
    }
    return isObject(tokens) || isString(tokens) ? tokens : null;
  }

  /** @internal */
  $cookieValue(name) {
    let cookies = document.cookie.split("; ");
    let cookie = name ? cookies.find(cookie => cookie.startsWith(name + "=")) : null;
    return cookie ? cookie.split("=")[1] : null;
  }

  /** @internal */
  $headers({
    language,
    body,
    blob
  }) {
    let headers = {
      Accept: blob ? "*/*" : "application/json"
    };
    if (body && !(body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }
    if (this.$config.client) {
      headers[clientHeader] = this.$config.client;
    }
    if (version) {
      headers[versionHeader] = version;
    }
    if (!isBrowser && this.$config.basicAuth) {
      headers["Authorization"] = "Basic " + this.$config.basicAuth;
    }
    let tokens = this.$userTokens();
    if (tokens) {
      let multiple = isObject(tokens);
      let bearer = multiple ? tokens.accessToken || tokens.idToken || tokens.identityToken : tokens;
      if (bearer) {
        let currentAuth = headers["Authorization"];
        headers["Authorization"] = (currentAuth ? currentAuth + ", " : "") + "Bearer " + bearer;
      }
      if (multiple) {
        if (tokens.refreshToken) {
          headers["X-Refresh"] = tokens.refreshToken;
        }
        if (tokens.accessToken) {
          headers["X-Access"] = tokens.accessToken;
        }
      }
    }
    if (language || this.$config.language) {
      headers["Accept-Language"] = language || this.$config.language;
    }
    if (this.$config.proxy) {
      let proxy = isString(this.$config.proxy) ? this.$config.proxy : defaultProxyPath;
      let proxyPath = proxy.replace(/^.*\/\/[^/]+/, "");
      if (proxyPath && proxyPath !== contextPath) {
        headers[prefixHeader] = proxyPath;
      }
    }
    headers["X-Trace"] = this.$config.session + "-" + ++this.$config.request;
    return headers;
  }

  /** @internal */
  $validate(response) {
    if (response.status >= 400) {
      //TODO: may need to reject with error json
      throw new Error(response.statusText);
    }
  }

  /** @internal */
  $fetch(method, path, parameters, data, {
    language,
    blob,
    revoke,
    unconverted,
    type,
    force,
    abortController
  } = {}) {
    let defer;
    let controller = abortController || new AbortController();
    if (this.$config.init || !isBrowser || force) {
      let url = this.$url(path, parameters);
      let headers = this.$headers({
        language,
        body: data,
        blob
      });
      let body = data && !(data instanceof FormData) ? JSON.stringify(data) : data;
      defer = fetch(url, {
        method,
        mode: "cors",
        credentials: "include",
        referrerPolicy: "strict-origin",
        headers,
        body,
        signal: controller.signal
      });
      if (blob) {
        defer = defer.then(response => {
          this.$validate(response);
          return response.status === 204 ? null : response.blob();
        }).then(results => {
          let value = results;
          if (results && !unconverted) {
            value = URL.createObjectURL(results);
            setTimeout(() => {
              URL.revokeObjectURL(value);
            }, revoke ? 10000 : 600000);
          }
          return value;
        });
      } else {
        defer = defer.then(response => {
          this.$validate(response);
          return response.status === 204 ? null : response.json();
        }).then(results => {
          return results && !unconverted ? this.$convertFrom(results, type) : results;
        });
      }
    } else {
      defer = delay().then(() => {
        let loader = this.$config.provider ? this.loadMaps(this.$config.provider) : this.loadConfiguration();
        return loader.then(() => {
          if (!this.$config.init) {
            console.warn("Unable to initialize session!");
          }
          return this.$fetch(method, path, parameters, data, {
            language,
            blob,
            revoke,
            unconverted,
            type,
            force: true,
            abortController: controller
          });
        });
      });
    }
    //TODO: error handler for specific errors?
    return Object.assign(defer, {
      abort: proxy(controller.abort || noop, controller)
    });
  }

  /** @internal */
  $get(path, parameters = null, options = {}) {
    return this.$fetch("GET", path, parameters, null, options);
  }

  /** @internal */
  $post(path, parameters = null, data = null, options = {}) {
    return this.$fetch("POST", path, parameters, data, options);
  }

  /** @internal */
  $put(path, parameters = null, data = null, options = {}) {
    return this.$fetch("PUT", path, parameters, data, options);
  }

  /** @internal */
  $delete(path, parameters = null, options = {}) {
    return this.$fetch("DELETE", path, parameters, null, options);
  }

  /** @internal */
  $getOrPost(path, parameters, options = {}) {
    let approxLength = path.length + JSON.stringify(parameters).length;
    if (approxLength < 2000) {
      return this.$get(path, parameters, options);
    } else {
      return this.$post(path, null, parameters, options);
    }
  }

  /** @internal */
  $loadScript(path, parameters, {
    language
  }) {
    if (isBrowser) {
      let url = this.$urlClient(path, parameters, {
        language
      });
      const script = document.createElement("script");
      script.async = true;
      script.referrerPolicy = "strict-origin";
      script.src = url;
      const head = document.getElementsByTagName("head")[0];
      head.appendChild(script);
      return script;
    }
  }

  /** @internal */
  $convertFrom(value, type) {
    let results = value;
    if (results && type) {
      switch (type) {
        case Convertible.LOCATION:
          results = deserializeLocation(results);
          break;
        case Convertible.CLUSTER:
          results = deserializeCluster(results);
          break;
      }
      if (this.$config.converter) {
        results = this.$config.converter.convertFrom(results, type);
      }
    }
    return results;
  }

  /** @internal */
  $convertTo(value, type) {
    let results = value;
    if (results && type) {
      if (this.$config.converter) {
        results = this.$config.converter.convertTo(results, type);
      }
      switch (type) {
        case Convertible.POSITION:
          if (!this.$config.converter || !isArray(results)) {
            results = Position.toGeoJSON(results);
          }
          break;
        case Convertible.BOUNDING_BOX:
          if (!this.$config.converter || !isArray(results)) {
            results = BoundingBox.toGeoJSON(results);
          }
          break;
        case Convertible.SIZE:
          results = Size.serialize(results);
          break;
      }
    }
    return results;
  }

  //endregion

  /**
   * Configure the MapsService for the project.
   * @param {Object} options
   * @param {boolean} options.production - Connect to the MapsService production lane.
   * @param {(boolean|string)} [options.proxy] - Proxy network traffic.
   * @param {string} options.client - Project's ESF client username.
   * @param {string} [options.clientPassword] - Project's ESF client password. Not to be used browser side.
   * @param {string} [options.language] - Language ISO for all response data.
   * @param {string} [options.basicAuth] - Project's ESF Basic Authorization. Not to be used browser side.
   * @param {(string|Object|function)} [options.oAuth] - End User's OAuth.
   * @param {*} [options.config] - For internal testing. Do Not Use.
   */
  configure({
    production,
    proxy,
    client,
    clientPassword,
    language,
    basicAuth,
    oAuth,
    ...config
  }) {
    const {
      develop
    } = config;
    Object.assign(this.$config, removeUndefined({
      production,
      develop,
      proxy,
      client,
      language,
      basicAuth: defaultEmpty(basicAuth, clientPassword ? btoa(`${client}:${clientPassword}`) : undefined),
      oAuth
    }));
    if (isEmpty(this.$config.client) || isEmpty(this.$config.production) && isEmpty(this.$config.proxy)) {
      console.warn("Maps: Configuration missing either 'client', 'production', or 'proxy' settings.");
    }
    if (isBrowser && this.$config.basicAuth) {
      throw new Error("Maps Basic Auth and/or Client Password should not be used from the browser.");
    }
  }
  ready() {
    return !!(isBrowser ? this.$config.client || this.$config.proxy : this.$config.basicAuth);
  }
  registerConverter(converter) {
    this.$config.converter = converter;
  }
  loadConfiguration({
    ip,
    language
  } = {}) {
    let promise = this.$config.configPromise;
    if (!promise) {
      let parameters = {
        ip
      };
      let options = {
        language,
        force: true
      };
      this.$config.configPromise = promise = this.$get("/scripts/configure", parameters, options).then(configure => {
        this.$refreshConfig({
          init: true,
          ...configure
        });
        return {
          coordinates: this.$convertFrom(this.$config.coordinates, Convertible.POSITION)
        };
      });
    }
    return promise;
  }
  loadMaps(provider, {
    libraries,
    ip,
    language,
    converter
  } = {}) {
    let promise = this.$config.providerPromise;
    if (!promise || provider !== this.$config.provider) {
      this.$config.provider = provider;
      if (converter) {
        this.registerConverter(converter);
      }
      let configPromise = this.loadConfiguration({
        ip,
        language
      });
      let providerPromise = new Promise(resolve => {
        if (provider) {
          let callback = `$MAPS_LOAD_${provider}_${this.$config.session}`;
          window[callback] = api => {
            delete window[callback];
            resolve({
              api
            });
          };
          this.$loadScript(`/scripts/${provider}`, {
            libraries,
            callback
          }, {
            language
          });
        } else {
          console.warn("No MapProviderType specified");
          resolve({});
        }
      });
      this.$config.providerPromise = promise = Promise.all([configPromise, providerPromise]).then(results => {
        return {
          api: results[1].api,
          coordinates: this.$convertFrom(this.$config.coordinates, Convertible.POSITION)
        };
      });
    }
    return promise;
  }
  getLegal() {
    //TODO: { language } = {}
    return new Promise((resolve, reject) => {
      if (this.$config.legal) {
        resolve(this.$config.legal);
      } else {
        //TODO: get from ajax call?
        reject();
      }
    });
  }

  //region Geocode

  geocodeAddress(address, {
    extent,
    country,
    phone,
    matches,
    unfiltered,
    limit,
    providers,
    language,
    unconverted
  } = {}) {
    let parameters = {
      address,
      extent: this.$convertTo(extent, Convertible.BOUNDING_BOX),
      country,
      phone,
      matches,
      unfiltered,
      limit,
      providers
    };
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    if (isObject(address)) {
      return this.$post("/geocode/address", null, parameters, options);
    } else {
      return this.$get("/geocode", parameters, options);
    }
  }
  reverseGeocode(coordinates, {
    matches,
    limit,
    providers,
    language,
    unconverted
  } = {}) {
    let parameters = {
      coordinates: this.$convertTo(coordinates, Convertible.POSITION),
      matches,
      limit,
      providers
    };
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    return this.$get("/geocode/reverse", parameters, options);
  }
  autocompleteGeocode(input, {
    extent,
    unfiltered,
    limit,
    providers,
    language,
    unconverted
  } = {}) {
    let parameters = {
      input,
      extent: this.$convertTo(extent, Convertible.BOUNDING_BOX),
      unfiltered,
      limit,
      providers
    };
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    return this.$get("/geocode/autocomplete", parameters, options);
  }
  geocodeIpAddress(ip = null, {
    limit,
    providers,
    language,
    unconverted
  } = {}) {
    let parameters = {
      ip,
      limit,
      providers
    };
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    if (ip) {
      return this.$get("/geocode/ip", parameters, options);
    } else {
      let promise = this.$config.ipPromise;
      if (!promise) {
        // prevent call from being called multiple times without a provided "ip"
        this.$config.ipPromise = promise = this.$get("/geocode/ip", parameters, options);
      }
      return promise;
    }
  }
  getIpCoordinates({
    sync,
    unconverted
  } = {}) {
    if (sync) {
      let coordinates = this.$config.coordinates;
      if (coordinates) {
        return unconverted ? coordinates : this.$convertFrom(coordinates, Convertible.POSITION);
      }
      return null;
    } else {
      return new Promise(resolve => {
        if (!this.$config.coordinates) {
          this.geocodeIpAddress().then(results => {
            if (results.length) {
              this.$config.coordinates = this.$convertTo(results[0].coordinates, Convertible.POSITION);
            }
            resolve(this.$config.coordinates);
          });
        } else {
          resolve(this.$config.coordinates);
        }
      }).then(coordinates => {
        return unconverted ? coordinates : this.$convertFrom(coordinates, Convertible.POSITION);
      });
    }
  }

  //endregion

  //region Navigation

  getDirections(origin, destination, {
    waypoints,
    modes,
    avoid,
    alternatives,
    depart,
    arrive,
    lengthUnits,
    timeUnits,
    compressed,
    providers,
    language,
    unconverted
  } = {}) {
    let parameters = {
      origin: serializeWaypoint(origin, this.$config.converter),
      destination: serializeWaypoint(destination, this.$config.converter),
      waypoints: serializeWaypoints(waypoints, this.$config.converter),
      modes,
      avoid,
      alternatives,
      depart,
      arrive,
      lengthUnits: serializeMeasurements(lengthUnits),
      timeUnits: serializeMeasurements(timeUnits),
      compressed,
      providers
    };
    let options = {
      language,
      unconverted,
      type: Convertible.ROUTE
    };
    return this.$getOrPost("/navigation/directions", parameters, options);
  }
  getIsochrone(origin, {
    times,
    distances,
    modes,
    avoid,
    alternatives,
    depart,
    arrive,
    compressed,
    providers,
    unconverted
  }) {
    let parameters = {
      origin: serializeWaypoint(origin, this.$config.converter),
      times: serializeMeasurements(times),
      distances: serializeMeasurements(distances),
      modes,
      avoid,
      alternatives,
      depart,
      arrive,
      compressed,
      providers
    };
    let options = {
      unconverted,
      type: Convertible.GEOMETRY_COLLECTION
    };
    return this.$getOrPost("/navigation/isochrone", parameters, options);
  }

  //endregion

  //region Geometry

  union(geometry, {
    reference,
    unconverted
  } = {}) {
    let data = {
      geometry: this.$convertTo(geometry, Convertible.GEOMETRY),
      reference
    };
    let options = {
      unconverted,
      type: Convertible.GEOMETRY_COLLECTION
    };
    return this.$post("/geometry/union", null, data, options);
  }
  cut(target, cutter, {
    reference,
    unconverted
  } = {}) {
    let data = {
      target: this.$convertTo(target, Convertible.GEOMETRY),
      cutter: this.$convertTo(cutter, Convertible.GEOMETRY),
      reference
    };
    let options = {
      unconverted,
      type: Convertible.GEOMETRY_COLLECTION
    };
    return this.$post("/geometry/cut", null, data, options);
  }
  reshape(target, reshaper, {
    reference,
    unconverted
  } = {}) {
    let data = {
      target: this.$convertTo(target, Convertible.GEOMETRY),
      reshaper: this.$convertTo(reshaper, Convertible.GEOMETRY),
      reference
    };
    let options = {
      unconverted,
      type: Convertible.GEOMETRY_COLLECTION
    };
    return this.$post("/geometry/reshape", null, data, options);
  }
  difference(input, difference, {
    reference,
    unconverted
  } = {}) {
    let data = {
      input: this.$convertTo(input, Convertible.GEOMETRY),
      difference: this.$convertTo(difference, Convertible.GEOMETRY),
      reference
    };
    let options = {
      unconverted,
      type: Convertible.GEOMETRY_COLLECTION
    };
    return this.$post("/geometry/difference", null, data, options);
  }
  differentiate(geometries, {
    reference,
    unconverted
  } = {}) {
    let data = {
      geometries: this.$convertTo(geometries, Convertible.GEOMETRY),
      reference
    };
    let options = {
      unconverted,
      type: Convertible.GEOMETRY_COLLECTION
    };
    return this.$post("/geometry/differentiate", null, data, options);
  }
  createTopology(geometry, border = null, {
    tolerance,
    reference,
    unconverted
  } = {}) {
    let data = {
      geometry: this.$convertTo(geometry, Convertible.GEOMETRY),
      border: this.$convertTo(border, Convertible.GEOMETRY),
      tolerance,
      reference
    };
    let options = {
      unconverted,
      type: Convertible.GEOMETRY_COLLECTION
    };
    return this.$post("/geometry/topology", null, data, options);
  }
  adjustTopology(topology, adjustment, border = null, {
    tolerance,
    reference,
    unconverted
  } = {}) {
    let data = {
      topology: this.$convertTo(topology, Convertible.GEOJSON),
      adjustment: this.$convertTo(adjustment, Convertible.GEOJSON),
      border: this.$convertTo(border, Convertible.GEOMETRY),
      tolerance,
      reference
    };
    let options = {
      unconverted,
      type: Convertible.FEATURE_COLLECTION
    };
    return this.$post("/geometry/topology/adjust", null, data, options);
  }
  repair(geometry, {
    tolerance,
    reference,
    unconverted
  } = {}) {
    let data = {
      geometry: this.$convertTo(geometry, Convertible.GEOMETRY),
      tolerance,
      reference
    };
    let options = {
      unconverted,
      type: Convertible.GEOMETRY_COLLECTION
    };
    return this.$post("/geometry/repair", null, data, options);
  }
  relation(geometry1, geometry2, {
    relation,
    reference
  } = {}) {
    let data = {
      geometry1: this.$convertTo(geometry1, Convertible.GEOMETRY),
      geometry2: this.$convertTo(geometry2, Convertible.GEOMETRY),
      relation,
      reference
    };
    return this.$post("/geometry/relation", null, data);
  }
  intersects(geometry, border = null, {
    reference,
    unconverted
  } = {}) {
    let data = {
      geometry: this.$convertTo(geometry, Convertible.GEOMETRY),
      border: this.$convertTo(border, Convertible.GEOMETRY),
      reference
    };
    let options = {
      unconverted,
      type: Convertible.GEOMETRY_COLLECTION
    };
    return this.$post("/geometry/intersects", null, data, options);
  }
  analyzeIntersects(geometry, exclusions = null, border = null, {
    units,
    reference,
    language,
    unconverted
  } = {}) {
    let data = {
      geometry: this.$convertTo(geometry, Convertible.GEOMETRY),
      exclusions: this.$convertTo(exclusions, Convertible.GEOMETRY),
      border: this.$convertTo(border, Convertible.GEOMETRY),
      units,
      reference
    };
    let options = {
      language,
      unconverted,
      type: Convertible.INTERSECTION
    };
    return this.$post("/geometry/intersects/analyze", null, data, options);
  }
  reanalyzeIntersects(intersections, exclusions = null, border = null, {
    units,
    reference,
    language,
    unconverted
  } = {}) {
    let data = {
      intersections: serializeIntersections(this.$convertTo(intersections, Convertible.INTERSECTION)),
      exclusions: this.$convertTo(exclusions, Convertible.GEOMETRY),
      border: this.$convertTo(border, Convertible.GEOMETRY),
      units,
      reference
    };
    let options = {
      language,
      unconverted,
      type: Convertible.INTERSECTION
    };
    return this.$post("/geometry/intersects/reanalyze", null, data, options);
  }
  centroids(geometry, {
    type,
    reference,
    unconverted
  } = {}) {
    let data = {
      geometry: this.$convertTo(geometry, Convertible.GEOMETRY),
      type,
      reference
    };
    let options = {
      unconverted,
      type: Convertible.GEOMETRY_COLLECTION
    };
    return this.$post("/geometry/centroids", null, data, options);
  }
  labels(geometry, {
    multiple,
    reference,
    unconverted
  } = {}) {
    let data = {
      geometry: this.$convertTo(geometry, Convertible.GEOMETRY),
      multiple,
      reference
    };
    let options = {
      unconverted,
      type: Convertible.POSITION_BOX
    };
    return this.$post("/geometry/labels", null, data, options);
  }
  parse(input, {
    provider,
    unconverted
  } = {}) {
    let parameters = {
      provider
    };
    let data = null;
    if (input instanceof File) {
      data = new FormData();
      data.append("file", input);
    } else if (isString(input)) {
      //TODO: parse URL
    }
    let options = {
      unconverted,
      type: Convertible.FEATURE_COLLECTION
    };
    return this.$post("/geometry/parse", parameters, data, options);
  }

  //endregion

  //region Layers

  getLayers({
    language
  } = {}) {
    let options = {
      language
    };
    return this.$get("/layers", null, options);
  }
  getLayer(type, {
    language
  } = {}) {
    let options = {
      language
    };
    return this.$get(`/layers/${type}`, null, options);
  }
  getLayersGroups({
    language
  } = {}) {
    let options = {
      language
    };
    return this.$get("/layers/groups", null, options);
  }
  getLayersGroup(type, {
    language
  } = {}) {
    let options = {
      language
    };
    return this.$get(`/layers/groups/${type}`, null, options);
  }

  //endregion

  //region Locations

  getLocations({
    layers,
    ids,
    filters,
    limit,
    start,
    children,
    associated,
    related,
    language,
    unconverted
  } = {}) {
    let parameters = refinableOperation(layers, ids, filters, limit, start, children, associated, related);
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    return this.$get("/locations", parameters, options);
  }
  getLocation(id, layer = null, {
    live,
    children,
    associated,
    related,
    language,
    unconverted
  } = {}) {
    let identity = compileIdentifiable(id, layer);
    let parameters = {
      live,
      ...refinableOperation(null, null, null, null, null, children, associated, related)
    };
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    return this.$get(`/locations/${identity}`, parameters, options);
  }
  createLocation(location, {
    language,
    unconverted
  } = {}) {
    let data = this.$convertTo(location, Convertible.LOCATION);
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    return this.$post("/locations", null, data, options);
  }
  updateLocation(location, {
    language,
    unconverted
  } = {}) {
    let identity = serializeIdentifiable(location);
    let data = this.$convertTo(location, Convertible.LOCATION);
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    return this.$put(`/locations/${identity}`, null, data, options);
  }
  deleteLocation(id, layer = null) {
    let identity = compileIdentifiable(id, layer);
    return this.$delete(`/locations/${identity}`);
  }
  searchLocations(query, {
    layers,
    ids,
    filters,
    extent,
    providers,
    limit,
    start,
    children,
    associated,
    related,
    language,
    unconverted
  } = {}) {
    let parameters = {
      query,
      ...refinableOperation(layers, ids, filters, limit, start, children, associated, related),
      extent: this.$convertTo(extent, Convertible.BOUNDING_BOX),
      providers: toArray(providers)
    };
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    return this.$getOrPost("/locations/search", parameters, options);
  }
  autocompleteLocations(input, {
    layers,
    ids,
    filters,
    extent,
    providers,
    limit,
    language,
    unconverted
  } = {}) {
    let parameters = {
      input,
      ...locationsOperation(layers, ids, filters),
      extent: this.$convertTo(extent, Convertible.BOUNDING_BOX),
      providers: toArray(providers),
      limit
    };
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    return this.$getOrPost("/locations/autocomplete", parameters, options);
  }
  getLocationsOverlay(extent, {
    size,
    zoom,
    layers,
    ids,
    filters,
    styles,
    unconverted
  }) {
    let parameters = {
      extent: this.$convertTo(extent, Convertible.BOUNDING_BOX),
      size: this.$convertTo(size, Convertible.SIZE),
      zoom,
      layers,
      ids: serializeIdentifiables(ids),
      filters: serializeFilters(filters),
      styles: serializeStyles(styles)
    };
    let options = {
      blob: true,
      revoke: true,
      unconverted
    };
    return this.$getOrPost("/locations/overlay", parameters, options);
  }
  getLocationsClusters(extent, {
    size,
    zoom,
    layers,
    ids,
    filters,
    emphasize,
    icon,
    unconverted
  }) {
    let parameters = {
      extent: this.$convertTo(extent, Convertible.BOUNDING_BOX),
      size: this.$convertTo(size, Convertible.SIZE),
      zoom,
      ...locationsOperation(layers, ids, filters),
      emphasize,
      icon: this.$convertTo(icon, Convertible.SIZE)
    };
    let options = {
      unconverted,
      type: Convertible.CLUSTER
    };
    return this.$getOrPost("/locations/clusters", parameters, options);
  }
  identifyLocationClusters(coordinates, {
    extent,
    size,
    zoom,
    layers,
    ids,
    filters,
    emphasize,
    icon,
    language,
    unconverted
  }) {
    let parameters = {
      coordinates: this.$convertTo(coordinates, Convertible.POSITION),
      extent: this.$convertTo(extent, Convertible.BOUNDING_BOX),
      size: this.$convertTo(size, Convertible.SIZE),
      zoom,
      ...locationsOperation(layers, ids, filters),
      emphasize,
      icon: this.$convertTo(icon, Convertible.SIZE)
    };
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    return this.$getOrPost("/locations/clusters/identify", parameters, options);
  }
  getLocationBoundary(id, layer = null, {
    generalize,
    offset,
    compressed,
    unconverted
  } = {}) {
    let identity = compileIdentifiable(id, layer);
    let parameters = {
      generalize,
      offset,
      compressed
    };
    let options = {
      unconverted,
      type: Convertible.MULTI_POLYGON
    };
    return this.$get(`/locations/${identity}/boundary`, parameters, options);
  }
  getLocationsBoundaries({
    layers,
    ids,
    filters,
    missing,
    generalize,
    offset,
    compressed,
    limit,
    start,
    children,
    associated,
    related,
    language,
    unconverted
  } = {}) {
    let parameters = {
      ...refinableOperation(layers, ids, filters, limit, start, children, associated, related),
      missing,
      generalize,
      offset,
      compressed
    };
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    return this.$getOrPost("/locations/boundaries", parameters, options);
  }
  unionLocationsBoundaries({
    layers,
    ids,
    filters,
    generalize,
    offset,
    compressed,
    unconverted
  } = {}) {
    let parameters = {
      ...locationsOperation(layers, ids, filters),
      generalize,
      offset,
      compressed
    };
    let options = {
      unconverted,
      type: Convertible.MULTI_POLYGON
    };
    return this.$getOrPost("/locations/boundaries/union", parameters, options);
  }
  getLocationExtent(id, layer = null, {
    unconverted
  } = {}) {
    let identity = compileIdentifiable(id, layer);
    let options = {
      unconverted,
      type: Convertible.BOUNDING_BOX
    };
    return this.$get(`/locations/${identity}/extent`, null, options);
  }
  getLocationsExtents({
    layers,
    ids,
    filters,
    missing,
    limit,
    start,
    children,
    associated,
    related,
    language,
    unconverted
  } = {}) {
    let parameters = {
      ...refinableOperation(layers, ids, filters, limit, start, children, associated, related),
      missing
    };
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    return this.$getOrPost("/locations/extents", parameters, options);
  }
  getLocationCentroids(id, layer = null, {
    type,
    multiple,
    unconverted
  } = {}) {
    let identity = compileIdentifiable(id, layer);
    let parameters = {
      type,
      multiple
    };
    let options = {
      unconverted,
      type: Convertible.POSITION
    };
    return this.$get(`/locations/${identity}/centroids`, parameters, options);
  }
  getLocationsCentroids({
    layers,
    ids,
    filters,
    missing,
    type,
    multiple,
    limit,
    start,
    children,
    associated,
    related,
    language,
    unconverted
  } = {}) {
    let parameters = {
      ...refinableOperation(layers, ids, filters, limit, start, children, associated, related),
      missing,
      type,
      multiple
    };
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    return this.$getOrPost("/locations/centroids", parameters, options);
  }
  getLocationLabels(id, layer = null, {
    multiple,
    unconverted
  } = {}) {
    let identity = compileIdentifiable(id, layer);
    let parameters = {
      multiple
    };
    let options = {
      unconverted,
      type: Convertible.POSITION_BOX
    };
    return this.$get(`/locations/${identity}/labels`, parameters, options);
  }
  getLocationsLabels({
    layers,
    ids,
    filters,
    missing,
    multiple,
    limit,
    start,
    children,
    associated,
    related,
    language,
    unconverted
  } = {}) {
    let parameters = {
      ...refinableOperation(layers, ids, filters, limit, start, children, associated, related),
      missing,
      multiple
    };
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    return this.$getOrPost("/locations/labels", parameters, options);
  }
  identifyLocations({
    coordinates,
    address,
    ip,
    extent,
    boundary,
    geometry,
    radius,
    nearest
  }, {
    layers,
    ids,
    filters,
    relation,
    reference,
    limit,
    start,
    children,
    associated,
    related,
    language,
    unconverted
  } = {}) {
    let parameters = {
      ...refinableOperation(layers, ids, filters, limit, start, children, associated, related),
      relation,
      reference
    };
    let options = {
      language,
      unconverted,
      type: Convertible.LOCATION
    };
    let path = "/locations/identify";
    if (geometry) {
      let data = {
        geometry: this.$convertTo(geometry, Convertible.GEOMETRY),
        ...parameters
      };
      return this.$post(path, null, data, options);
    } else {
      Object.assign(parameters, {
        coordinates: this.$convertTo(coordinates, Convertible.POSITION),
        address,
        ip,
        extent: this.$convertTo(extent, Convertible.BOUNDING_BOX),
        boundary: serializeIdentifiable(boundary),
        radius: serializeMeasurement(radius),
        nearest
      });
      return this.$getOrPost(path, parameters, options);
    }
  }
  countLocations({
    coordinates,
    address,
    ip,
    extent,
    boundary,
    geometry,
    radius
  }, {
    layers,
    ids,
    filters,
    relation,
    reference
  } = {}) {
    let parameters = {
      ...locationsOperation(layers, ids, filters),
      relation,
      reference
    };
    let path = "/locations/count";
    if (geometry) {
      let data = {
        geometry: this.$convertTo(geometry, Convertible.GEOMETRY),
        ...parameters
      };
      return this.$post(path, null, data);
    } else {
      Object.assign(parameters, {
        coordinates: this.$convertTo(coordinates, Convertible.POSITION),
        address,
        ip,
        extent: this.$convertTo(extent, Convertible.BOUNDING_BOX),
        boundary: serializeIdentifiable(boundary),
        radius: serializeMeasurement(radius)
      });
      return this.$getOrPost(path, parameters);
    }
  }
  contactLocation(id, layer = null, {
    subject,
    message,
    senderName,
    senderEmail,
    senderPhone,
    referrerUrl,
    language
  } = {}) {
    let identity = compileIdentifiable(id, layer);
    let data = {
      subject,
      message,
      senderName,
      senderEmail,
      senderPhone,
      referrerUrl: referrerUrl || (isBrowser ? window.location.href : null)
    };
    let options = {
      language
    };
    return this.$post(`/locations/contact/${identity}`, null, data, options);
  }
  getLocationsFields({
    language
  } = {}) {
    let options = {
      language
    };
    return this.$get("/locations/fields", null, options);
  }
  getLocationsField(field, {
    language
  } = {}) {
    let options = {
      language
    };
    return this.$get(`/locations/fields/${field}`, null, options);
  }
  getLocationsFieldValues(field, {
    layers,
    ids,
    filters,
    includeNull,
    language
  } = {}) {
    let parameters = {
      ...locationsOperation(layers, ids, filters),
      includeNull
    };
    let options = {
      language
    };
    return this.$getOrPost(`/locations/fields/${field}/values`, parameters, options);
  }

  //endregion

  //region Media

  getLocationMedia(id, layer, type, {
    index,
    category,
    subCategory,
    size,
    imageSize,
    preserveRatio,
    unconverted
  } = {}) {
    let identity = compileIdentifiable(id, layer);
    let parameters = {
      type,
      index,
      category,
      subCategory,
      size,
      imageSize: this.$convertTo(imageSize, Convertible.SIZE),
      preserveRatio
    };
    let options = {
      blob: true,
      unconverted
    };
    return this.$get(`/media/locations/${identity}`, parameters, options);
  }
  getLocationMediaUrl(id, layer, type, {
    index,
    category,
    subCategory,
    size,
    imageSize,
    preserveRatio,
    response
  } = {}) {
    let identity = compileIdentifiable(id, layer);
    let parameters = {
      type,
      index,
      category,
      subCategory,
      size,
      imageSize: this.$convertTo(imageSize, Convertible.SIZE),
      preserveRatio,
      response
    };
    return this.$urlClient(`/media/locations/${identity}`, parameters);
  }
  getLocationDefaultImage(id, layer = null, {
    size,
    preserveRatio,
    unconverted
  } = {}) {
    let identity = compileIdentifiable(id, layer);
    let parameters = {
      size: this.$convertTo(size, Convertible.SIZE),
      preserveRatio
    };
    let options = {
      blob: true,
      unconverted
    };
    return this.$get(`/media/locations/${identity}/image`, parameters, options);
  }
  getLocationMediaCatalog(id, layer = null, {
    type,
    index,
    category,
    subCategory,
    size,
    imageSize,
    preserveRatio,
    unconverted
  } = {}) {
    let identity = compileIdentifiable(id, layer);
    let parameters = {
      type,
      index,
      category,
      subCategory,
      size
    };
    let defer = this.$get(`/media/catalog/locations/${identity}`, parameters);
    if (!unconverted) {
      defer = defer.then(results => this.loadLocationMedia(id, layer, results, {
        imageSize,
        preserveRatio
      }));
    }
    return defer;
  }
  loadLocationMedia(id, layer, media, {
    imageSize,
    preserveRatio
  } = {}) {
    let defers = media.map(entry => this.getLocationMedia(id, layer, entry.type, {
      imageSize: this.$convertTo(imageSize, Convertible.SIZE),
      preserveRatio,
      ...entry
    }).then(result => Object.assign({}, entry, {
      media: result
    })));
    return Promise.all(defers);
  }

  //endregion

  //region Icons

  getIcon({
    type,
    format,
    layer,
    legend,
    symbol,
    text,
    color,
    foreground,
    border,
    invert,
    scale,
    unconverted
  }) {
    let parameters = {
      type,
      format,
      layer,
      legend,
      symbol,
      text,
      color: serializeColor(color),
      foreground: serializeColor(foreground),
      border: serializeColor(border),
      invert,
      scale
    };
    let options = {
      blob: true,
      unconverted
    };
    return this.$get("/icons", parameters, options);
  }
  getIconUrl({
    type,
    format,
    layer,
    legend,
    symbol,
    text,
    color,
    foreground,
    border,
    invert,
    scale
  }) {
    let parameters = {
      type,
      format,
      layer,
      legend,
      symbol,
      text,
      color: serializeColor(color),
      foreground: serializeColor(foreground),
      border: serializeColor(border),
      invert,
      scale
    };
    return this.$urlClient("/icons", parameters);
  }

  //endregion

  //region Maps

  getMapImage({
    view,
    coordinates,
    size,
    zoom,
    heading,
    pitch,
    type,
    scale,
    layers,
    markers,
    paths,
    providers,
    response = ResponseType.REDIRECT,
    language
  } = {}) {
    let parameters = {
      view,
      coordinates: this.$convertTo(coordinates, Convertible.POSITION),
      size: this.$convertTo(size, Convertible.SIZE),
      zoom,
      heading,
      pitch,
      type,
      scale,
      layers,
      markers: this.$convertTo(markers, Convertible.MULTI_POINT),
      paths: this.$convertTo(paths, Convertible.MULTI_LINE_STRING),
      providers,
      response
    };
    let options = {
      language
    };
    return this.$urlClient("/maps/image", parameters, options);
  }
  getMapUrl({
    view,
    coordinates,
    zoom,
    type,
    query,
    origin,
    destination,
    waypoints,
    modes,
    heading,
    pitch,
    providers,
    response = ResponseType.REDIRECT,
    language
  } = {}) {
    let parameters = {
      view,
      coordinates: this.$convertTo(coordinates, Convertible.POSITION),
      zoom,
      type,
      query,
      origin: serializeWaypoint(origin, this.$config.converter),
      destination: serializeWaypoint(destination, this.$config.converter),
      waypoints: serializeWaypoints(waypoints, this.$config.converter),
      modes,
      heading,
      pitch,
      providers,
      response
    };
    let options = {
      language
    };
    return this.$urlClient("/maps/url", parameters, options);
  }
  getMapSource(id, provider, {
    language
  } = {}) {
    let options = {
      language
    };
    return this.$get(`/maps/sources/${provider}/${id}`, null, options);
  }
  getMapSourceLegendUrl(id, provider) {
    return this.$urlClient(`/maps/sources/${provider}/${id}/legend`);
  }
  getMapProviders({
    language
  } = {}) {
    let options = {
      language
    };
    return this.$get(`/maps/providers`, null, options);
  }
  getMapProvider(type, {
    language
  } = {}) {
    let options = {
      language
    };
    return this.$get(`/maps/providers/${type}`, null, options);
  }
  getMapProviderLogoUrl(type, {
    response
  } = {}) {
    let parameters = {
      response
    };
    return this.$urlClient(`/maps/providers/${type}/logo`, parameters);
  }
  getMapProviderWebsiteUrl(type, {
    coordinates,
    zoom,
    response = ResponseType.REDIRECT,
    language
  } = {}) {
    let parameters = {
      coordinates: this.$convertTo(coordinates, Convertible.POSITION),
      zoom,
      response
    };
    let options = {
      language
    };
    return this.$urlClient(`/maps/providers/${type}/website`, parameters, options);
  }

  //endregion

  //region Households

  getHouseholdsGeographicCenter(units, {
    unconverted
  } = {}) {
    let parameters = {
      units: serializeIdentifiables(units)
    };
    let options = {
      unconverted,
      type: Convertible.POSITION
    };
    return this.$get(`/households/center`, parameters, options);
  }

  //endregion

  //region Strings

  getStrings(keys, {
    language
  } = {}) {
    return new Promise(resolve => {
      const compile = () => {
        return keys.reduce((obj, key) => {
          obj[key] = this.$config.strings[key];
          return obj;
        }, {});
      };
      let queue = keys.filter(key => !this.$config.strings[key]);
      if (queue.length) {
        this.$get("/strings", {
          keys
        }, {
          language
        }).then(results => {
          Object.assign(this.$config.strings, results);
          resolve(compile());
        });
      } else {
        resolve(compile());
      }
    });
  }
  getString(key, options = {}) {
    return this.getStrings([key], options).then(results => {
      return results[key];
    });
  }

  //endregion
}
const MapsService = new $MapsService();
Object.freeze(MapsService);
export default MapsService;
export * from "@churchofjesuschrist/maps-models";
export * from "./components/Serializer";